import { Component, ElementRef, Renderer2, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { FormCommandHandlerResolver } from '../../../core/service/command/form-command-handler-resolver.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { ActivatedRoute } from '@angular/router';
import { standardCommand } from '../../../core/model/command';
import { EsignatureErrandService } from '../../service/data/esignature-errand.service';
import { ESignatureContext, ESignatureErrand } from '../../model/esignature-errand';
import { SelectionItem } from '../../../core/component/select/select.component';
import {
  ESignatory,
  InformationChannelKind,
  SignatureReminderSettingKind,
  SignatureTypeKind,
  SigningRoleKind,
} from '../../model/esignatory';
import { DatePipe } from '@angular/common';
import { FileService, PDFValidator } from '../../../core/service/file.service';
import { LocalDate } from '../../../core/model/local-date';
import { Company } from '../../model/company';
import { CompanyService } from '../../service/data/company.service';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { dateTimeString } from '../../../core/helper/date-time';
import { ESignatureErrandCommandHandlerResolver } from '../../service/command/esignature-errand-command-handler-resolver.service';
import { esignatureErrandCommand } from '../../command/esignature-errand.command';
import { OpenESignatureDocumentCommandHandler } from '../../service/command/open-esignature-document-command-handler.service';
import { CancelESignErrandCommandHandler } from '../../service/command/cancel-esign-errand-command-handler.service';
import { configureESignatoryContactValidation } from '../../helper/esignatory-form-validation';

@Component({
    selector: 'app-esignature-errand-form',
    templateUrl: './esignature-errand-form.component.html',
    styleUrls: ['./esignature-errand-form.component.scss'],
    providers: [
        DeleteFormCommandHandler,
        FormCommandHandlerResolver,
        ESignatureErrandCommandHandlerResolver,
        OpenESignatureDocumentCommandHandler,
        CancelESignErrandCommandHandler,
        { provide: DataService, useExisting: EsignatureErrandService },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ESignatureErrandFormComponent extends FormComponentBase<ESignatureErrand> {
  constructor(
    private _fb: FormBuilder,
    private _cdf: ColumnDefinitionFactory,
    private _fc: FormControlExFactory,
    private companyService: CompanyService,
    activatedRoute: ActivatedRoute,
    commandDispatcher: CommandDispatcher,
    handlerResolver: ESignatureErrandCommandHandlerResolver,
    service: EsignatureErrandService,
    private _fileService: FileService,
    private _renderer: Renderer2,
    messageService: MessageService,
    navigation: NavigationService,
    private _fileValidator: PDFValidator
  ) {
    super(activatedRoute, service, messageService, navigation, commandDispatcher, handlerResolver);

    this.setDefaults = true;

    this.addDeleteMenu = false;
    this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.SHOW_DOCUMENTS', esignatureErrandCommand.openESignatureDocument);
    this.addMenuItemDefinition('ESIGNATURE.CANCEL_ERRAND', esignatureErrandCommand.cancelESignatureErrand);
    this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);
  }

  @ViewChild('uploadFile') uploadFileElement: ElementRef;

  eSignatoryColumnDefinition: ColumnDefinition[];

  protected createModel(): ESignatureErrand {
    return new ESignatureErrand();
  }

  civicNumberMask = /^(\d{8}\-\d{0,4})|(\d{1,8})/;
  readonly civicNumberMaskFormat: string = 'ÅÅÅÅMMDD-NNNN';

  showEmail(sform: FormGroup): boolean {
    return (
      sform.value.informationChannel === InformationChannelKind.Email ||
      sform.value.informationChannel === InformationChannelKind.EmailAndSms
    );
  }

  showPhone(sform: FormGroup): boolean {
    return (
      sform.value.informationChannel === InformationChannelKind.Sms || sform.value.informationChannel === InformationChannelKind.EmailAndSms
    );
  }

  companies: SelectionItem[] = [];

  protected getFormGroup(): FormGroup {
    this.form = this._fb.group({
      id: this._fc.createHidden(this.dataContext.id),
      companyId: this._fc.createMandatory('RENTAL_OBJECT_FORM.COMPANY_LABEL', this.dataContext.companyId),
      companyCaption: this._fc.createReadonly('RENTAL_OBJECT_FORM.COMPANY_LABEL', this.dataContext.companyNumberAndName),
      statusText: this._fc.createReadonly('COMMON.STATUS', this.dataContext.statusText),
      cancelledAt: this._fc.createReadonly('ESIGNATURE.CANCELLED_AT', dateTimeString(this.dataContext.cancelledAt)),
      completedAt: this._fc.createReadonly('ESIGNATURE.COMPLETED_AT', dateTimeString(this.dataContext.completedAt)),
      createdAt: this._fc.createReadonly('ESIGNATURE.CREATED_AT', dateTimeString(this.dataContext.createdAt)),
      signatureContext: this._fc.createReadonly('ESIGNATURE.CONTEXT', this.dataContext.signatureContext),
      errandDescription: this._fc.createMandatory('ESIGNATURE.DESCRIPTION', this.dataContext.errandDescription),
      informWhenSigned: this._fc.create('ESIGNATURE.INFORM_WHEN_SIGNED', this.dataContext.informWhenSigned),
      informEmailAddress: this._fc.create('ESIGNATURE.INFORM_EMAIL_ADRESSES', this.dataContext.informEmailAddress),
      leaseAgreementNumber: this._fc.createReadonly(
        'LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER',
        this.dataContext.leaseAgreementNumber,
        'ESIGNATURE.HELP_TEXT.RELATES_TO_LEASE_AGREEMENT'
      ),
      expirationDate: this._fc.createMandatory(
        'ESIGNATURE.EXPIRATION_DATE',
        this.dataContext.expirationDate,
        'ESIGNATURE.HELP_TEXT.EXPIRATION_DATE'
      ),
      signatories: this._fb.array([]),
      document: this._fc.create('', null),
      documentCaption: this._fc.createHidden(''),
    });
    if (!this.isNew) {
      for (let signatory of this.dataContext.signatories) {
        this.addSignatory(signatory);
      }
    }
    return this.form;
  }

  signatureContexts = [
    new SelectionItem('ESIGNATURE.CONTEXT_TYPES.GENERAL_DOCUMENT', ESignatureContext.GeneralDocument),
    new SelectionItem('ESIGNATURE.CONTEXT_TYPES.APPROVE_LEASE', ESignatureContext.ApproveLeaseAgreement),
    new SelectionItem('ESIGNATURE.CONTEXT_TYPES.TERMINATE_LEASE', ESignatureContext.TerminateLeaseAgreement),
  ];

  signatureTypeSelection = [
    new SelectionItem('ESIGNATURE.SIGNATURE_TYPE.BANKID', SignatureTypeKind.BankId),
    new SelectionItem('ESIGNATURE.SIGNATURE_TYPE.CLICK', SignatureTypeKind.Click),
  ];

  informationChannels = [
    new SelectionItem('ESIGNATURE.INFORMATION_CHANNEL.EMAIL', InformationChannelKind.Email),
    new SelectionItem('ESIGNATURE.INFORMATION_CHANNEL.SMS', InformationChannelKind.Sms),
    new SelectionItem('ESIGNATURE.INFORMATION_CHANNEL.EMAIL_AND_SMS', InformationChannelKind.EmailAndSms),
  ];

  signingRoles = [
    new SelectionItem('ESIGNATURE.SIGNING_ROLE.NOT_SPECIFIED', SigningRoleKind.NotSpecified),
    new SelectionItem('ESIGNATURE.SIGNING_ROLE.COMPANY_REPRESENTATIVE', SigningRoleKind.CompanyRepresentative),
    new SelectionItem('ESIGNATURE.SIGNING_ROLE.TENANT', SigningRoleKind.Tenant),
    new SelectionItem('LEASE_AGREEMENT.CREDITOR', SigningRoleKind.Guarantor),
  ];

  signatureReminderSettingSelection = [
    new SelectionItem('ESIGNATURE.SIGNATURE_REMINDER_SETTING.NO_REMINDERS', SignatureReminderSettingKind.NoReminders),
    new SelectionItem('ESIGNATURE.SIGNATURE_REMINDER_SETTING.ONCE_A_DAY', SignatureReminderSettingKind.OnceADay),
  ];

  allowNewSignatory = true;
  allowDeleteSignatory = true;
  hasLinkToLeaseAgreement = false;

  newSignatory() {
    let signatory = new ESignatory();
    signatory.signingRole = SigningRoleKind.NotSpecified;
    this.addSignatory(signatory);
  }

  deleteSignatory(index: number) {
    const signatories = this.form.controls['signatories'] as FormArray;
    signatories.removeAt(index);
  }

  onFileChange(files: FileList) {
    if (files && files.length === 1) {
      if (this._fileValidator.validateDocument(files[0]) === null) {
        this.form.controls['document'].setValue(files[0]);
        let fileName = files[0].name.substr(0, files[0].name.lastIndexOf('.'));
        this.form.controls['errandDescription'].setValue('Signering av ' + fileName);
        this.form.controls['documentCaption'].setValue(fileName);
      } else {
        this._renderer.setProperty(this.uploadFileElement.nativeElement, 'value', null);
        this.form.controls['errandDescription'].reset();
        this.form.controls['documentCaption'].reset();
      }
    } else {
      throw new Error('Invalid operation.');
    }
  }

  get signatoryForms(): FormGroup[] {
    return (this.form.controls['signatories'] as FormArray).controls.map(c => c as FormGroup);
  }

  private addSignatory(signatory: ESignatory) {
    const signatories = this.form.controls['signatories'] as FormArray;
    let allowWrite = !signatory.signedAt && !signatory.rejectedAt;
    let options = { isReadonly: !allowWrite };
    const signatoryForm = this._fb.group({
        id: this._fc.createHidden(signatory.id),
        firstReadAt: this._fc.createReadonly('ESIGNATURE.SIGNATORY.FIRST_READ', signatory.firstReadAt),
        civicNumber: this._fc.createWithOptions('ESIGNATURE.SIGNATORY.CIVIC_NUMBER', signatory.civicNumber, { isReadonly: !allowWrite }),
        email: this._fc.createWithOptions('ESIGNATURE.SIGNATORY.EMAIL', signatory.email, { ...options, validator: Validators.email }),
        informationChannel: this._fc.createWithOptions('ESIGNATURE.SIGNATORY.INFORMATION_CHANNEL', signatory.informationChannel, {
          isReadonly: !this.isNew && !allowWrite,
        }),
        signatureType: this._fc.createMandatory('ESIGNATURE.SIGNATORY.SIGNATURE_TYPE', signatory.signatureType),
        informationSentAt: this._fc.createReadonly('ESIGNATURE.SIGNATORY.INFO_SENT_AT', signatory.informationSentAt),
        name: this._fc.createWithOptions('ESIGNATURE.SIGNATORY.NAME', signatory.name, { isReadonly: !allowWrite, required: true }),
        phone: this._fc.createWithOptions('ESIGNATURE.SIGNATORY.PHONE', signatory.phone, options),
        rejectedAt: this._fc.createReadonly('ESIGNATURE.SIGNATORY.REJECTED_AT', signatory.rejectedAt),
        signedAt: this._fc.createReadonly('ESIGNATURE.SIGNATORY.SIGNED_AT', signatory.signedAt),
        signOrder: this._fc.create('ESIGNATURE.SIGNATORY.SIGN_ORDER', signatory.signOrder),
        signingRole: this._fc.createMandatory('COMMON.ROLE', signatory.signingRole),
        signUrl: this._fc.createReadonly('ESIGNATURE.ERRAND_URL', signatory.signUrl),
        emailDeliveryStatusText: this._fc.createReadonly('COMMON.EMAIL_DELIVERY_STATUS_EMAIL', signatory.emailDeliveryStatusText),
        signatureReminderSetting: this._fc.createMandatory(
          'ESIGNATURE.SIGNATORY.SIGNATURE_REMINDER_SETTING',
          signatory.signatureReminderSetting
        ),
      });
    signatories.push(signatoryForm);
    this.AddSubscription(configureESignatoryContactValidation(signatoryForm));
  }

  protected init(data: ESignatureErrand) {
    super.init(data);

    if (this.dataContext.isNew) {
      let signatory = new ESignatory();
      signatory.civicNumber = this.dataContext.currentUserCivicNumber;
      signatory.name = this.dataContext.currentUserPersonName;
      signatory.email = this.dataContext.currentUserEmailAddress;
      signatory.phone = this.dataContext.currentUserPhone;
      signatory.signingRole = SigningRoleKind.CompanyRepresentative;
      signatory.signatureType = this.dataContext.currentUserCivicNumber ? SignatureTypeKind.BankId : SignatureTypeKind.Click;
      this.addSignatory(signatory);
    }

    this.allowDeleteSignatory = !this.dataContext.cancelledAt && !this.dataContext.completedAt;
    this.allowNewSignatory = this.allowDeleteSignatory;
    this.hasLinkToLeaseAgreement = this.dataContext.leaseAgreementNumber && this.dataContext.leaseAgreementNumber !== '';

    this.companyService.getList().subscribe(data => {
      this.companies = data.map((item: Company) => {
        return { name: item.toString(), value: item.id };
      });

      const companyControl = this.form.controls['companyId'];
      const hasSelectedCompany = this.companies.some(company => company.value === companyControl.value);

      // Defaults can contain a non-selectable sentinel value. A placeholder is not a company selection.
      if (this.isNew && companyControl.value && !hasSelectedCompany) {
        companyControl.setValue(undefined);
      }

      if (this.companies.length == 1 && this.isNew) {
        companyControl.setValue(this.companies[0].value);
      }
    });
  }
}
